import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from 'context/AppContext';
import { ActionTypes } from 'context/AppContext';

/**
 * 通用数据请求 Hook
 * @param {string} cacheKey - 缓存键名
 * @param {Function} fetchFn - 数据获取函数
 * @param {object} options - 配置选项
 */
const useDataRequest = (cacheKey, fetchFn, options = {}) => {
  const { state, dispatch } = useAppContext();
  const [data, setData] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);

  // 使用 ref 追踪是否已经初始化,避免重复请求
  const hasFetchedRef = useRef(false);

  const {
    useCache = true, // 是否使用缓存
    immediate = true, // 是否立即执行
    params = {},
  } = options;

  // 执行数据请求
  const execute = useCallback(
    async (...args) => {
      // 检查缓存
      if (useCache && state[cacheKey]) {
        setData(state[cacheKey]);
        return state[cacheKey];
      }

      try {
        setLocalLoading(true);
        dispatch({ type: ActionTypes.SET_LOADING, payload: true });
        dispatch({ type: ActionTypes.CLEAR_ERROR });

        // 执行请求
        const result = await fetchFn(...args);

        // 更新状态
        setData(result);
        setError(null);

        // 缓存数据
        if (useCache) {
          dispatch({
            type: ActionTypes.SET_CACHE,
            payload: { key: cacheKey, data: result },
          });
        }

        return result;
      } catch (err) {
        const errorMsg = err.message || '数据加载失败';
        setError(errorMsg);
        dispatch({ type: ActionTypes.SET_ERROR, payload: errorMsg });
        throw err;
      } finally {
        setLocalLoading(false);
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
      }
    },
    // 移除 state 依赖,避免无限循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheKey, fetchFn, useCache, dispatch],
  );

  // 刷新数据（忽略缓存）
  const refresh = useCallback(
    async (...args) => {
      // 临时禁用缓存以强制刷新
      const originalCache = state[cacheKey];
      if (originalCache) {
        dispatch({
          type: ActionTypes.SET_CACHE,
          payload: { key: cacheKey, data: null },
        });
      }

      const result = await execute(...args);

      // 恢复原缓存（如果刷新失败）
      if (!result && originalCache) {
        dispatch({
          type: ActionTypes.SET_CACHE,
          payload: { key: cacheKey, data: originalCache },
        });
      }

      return result;
    },
    [cacheKey, state, dispatch, execute],
  );

  // 清除缓存
  const clearCache = useCallback(() => {
    dispatch({
      type: ActionTypes.SET_CACHE,
      payload: { key: cacheKey, data: null },
    });
    setData(null);
  }, [cacheKey, dispatch]);

  // 自动执行 - 只在组件挂载时执行一次
  useEffect(() => {
    if (immediate && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      execute({ ...params });
    }

    // 清理函数:组件卸载时重置标记
    return () => {
      hasFetchedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空依赖数组,只在挂载时执行

  return {
    data,
    loading: localLoading || state.loading,
    error,
    execute,
    refresh,
    clearCache,
  };
};

export default useDataRequest;
